import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AtSign, ArrowUpRight } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loading, EmptyState } from "@/components/common/Loading";
import { useRealtime } from "@/context/RealtimeContext";
import { timeAgo } from "@/lib/timeago";

function NotedRow({ m }) {
  const a = m.actor || {};
  const actorHref = a.slug ? `/p/${a.slug}` : (a.handle ? `/@${a.handle}` : null);
  const ActorName = actorHref
    ? <Link to={actorHref} className="font-[640] text-foreground hover:underline" data-testid={`noted-actor-${m.id}`}>{a.display_name || "Member"}</Link>
    : <span className="font-[640] text-foreground">{a.display_name || "Member"}</span>;
  return (
    <div className="flex items-start gap-3 py-4 first:pt-0" data-testid={`noted-item-${m.id}`}>
      <Avatar className="h-10 w-10 shrink-0">
        {a.headshot_url && <AvatarImage src={fileUrl(a.headshot_url)} alt="" />}
        <AvatarFallback className="bg-teal-50 text-sm text-teal-700">{(a.display_name || "?")[0]}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-6 text-foreground">
          {ActorName} mentioned{" "}
          <Link to={`/@${m.handle}`} className="font-[600] text-primary hover:underline" data-testid={`noted-handle-${m.id}`}>@{m.handle}</Link>{" "}
          <span className="text-muted-foreground">in {m.source_label || "a post"}</span>
        </p>
        {m.excerpt && (
          <p className="mt-1 line-clamp-2 border-l-2 border-border pl-3 text-sm italic text-muted-foreground" data-testid={`noted-excerpt-${m.id}`}>
            “{m.excerpt}”
          </p>
        )}
        <div className="mt-2 flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{timeAgo(m.created_at)}</span>
          {m.link && (
            <Link to={m.link} className="inline-flex items-center gap-1 text-xs font-[560] text-primary hover:underline" data-testid={`noted-view-${m.id}`}>
              View <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Noted() {
  const [data, setData] = useState(null);
  const { markSectionRead } = useRealtime();

  useEffect(() => {
    let active = true;
    (async () => {
      try { const { data } = await api.get("/mentions/mine?limit=100"); if (active) setData(data); }
      catch (e) { if (active) setData({ items: [], total: 0 }); }
    })();
    const t = setTimeout(() => { if (markSectionRead) markSectionRead("noted"); }, 0);
    return () => { active = false; clearTimeout(t); };
  }, [markSectionRead]);

  return (
    <div className="mx-auto max-w-3xl" data-testid="noted-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50 text-primary"><AtSign className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-[720] tracking-[-0.02em] text-foreground">Noted</h1>
          <p className="text-sm text-muted-foreground">Every time someone @mentioned you or one of your profiles.</p>
        </div>
      </div>

      <Card className="mt-6 border-0 p-6">
        {data === null ? (
          <div className="py-16"><Loading /></div>
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={AtSign}
            title="No mentions yet"
            description="When someone tags you with your @handle in a post or comment, it'll show up here."
            testId="noted-empty"
          />
        ) : (
          <div className="divide-y divide-border">
            {data.items.map((m) => <NotedRow key={m.id} m={m} />)}
          </div>
        )}
      </Card>
    </div>
  );
}
